import { ReactElement } from 'react'
import { Button, Icon } from '@dynamo-caffeina-dev/airc-ui-kit'
import { PaginationType } from '@/types/PaginationType'
import { formatDate } from '@/helpers/date'

interface TemporalNavigationProps {
  meta: PaginationType
  timeDirection: 'prossimi' | 'passati'
  monthsCount: number
  selectedRegion: string
  regions?: [string, string][]
  showTimeDirection?: boolean
  onTimeDirectionChange: (direction: 'prossimi' | 'passati') => void
  onMonthsCountChange: (count: number) => void
  onRegionChange: (region: string) => void
  onPeriodChange: (direction: 'previous' | 'next') => void
  loading?: boolean
  className?: string
}

export default function TemporalNavigation({
  meta,
  timeDirection,
  monthsCount,
  selectedRegion,
  regions = [],
  showTimeDirection = true,
  onTimeDirectionChange,
  onMonthsCountChange,
  onRegionChange,
  onPeriodChange,
  loading = false,
  className = '',
}: TemporalNavigationProps): ReactElement {
  const { current_page, last_page, from, to } = meta

  const monthOptions = [1, 2, 3, 4, 6, 12]

  return (
    <div className={`temporal-navigation ${className}`}>
      {/* Filters */}
      <div className='temporal-navigation__filters'>
        <div className='temporal-navigation__filter-group'>
          <span className='temporal-navigation__label'>
            {showTimeDirection ? 'Eventi dei' : 'Eventi dei prossimi'}
          </span>

          {showTimeDirection && (
            <select
              className='temporal-navigation__select'
              value={timeDirection}
              onChange={(e) =>
                onTimeDirectionChange(e.target.value as 'prossimi' | 'passati')
              }
              disabled={loading}
            >
              <option value='prossimi'>prossimi</option>
              <option value='passati'>passati</option>
            </select>
          )}

          <select
            className='temporal-navigation__select'
            value={monthsCount}
            onChange={(e) => onMonthsCountChange(Number(e.target.value))}
            disabled={loading}
          >
            {monthOptions.map((count) => (
              <option
                key={count}
                value={count}
              >
                {count}
              </option>
            ))}
          </select>

          <span className='temporal-navigation__label'>mesi nella regione</span>

          {regions.length ? (
            <select
              className='temporal-navigation__select'
              value={selectedRegion}
              onChange={(e) => onRegionChange(e.target.value)}
              disabled={loading}
            >
              <option value=''>Tutte le regioni</option>
              {regions.map(([value, label]) => (
                <option
                  key={value}
                  value={value}
                >
                  {label}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </div>

      {/* Navigation */}
      <div className='temporal-navigation__nav'>
        <div className='temporal-navigation__info'>
          Visualizzati{' '}
          <strong>{typeof from === 'string' ? formatDate(from) : from}</strong>{' '}
          — <strong>{typeof to === 'string' ? formatDate(to) : to}</strong>
        </div>

        <div className='temporal-navigation__controls'>
          <Button
            animate={false}
            size='small'
            variant='negative'
            className='temporal-navigation__controls__button'
            onClick={() => onPeriodChange('previous')}
            disabled={loading || current_page === 1}
            aria-label='Periodo precedente'
          >
            <Icon icon='ArrowLeft' />{' '}
            <span className='temporal-navigation__controls__button__label'>
              Periodo precedente
            </span>
          </Button>

          <Button
            animate={false}
            size='small'
            variant='negative'
            className={'temporal-navigation__controls__button'}
            onClick={() => onPeriodChange('next')}
            disabled={loading || current_page === last_page}
            aria-label='Periodo successivo'
          >
            <span className='temporal-navigation__controls__button__label'>
              Periodo successivo
            </span>{' '}
            <Icon icon='ArrowRight' />
          </Button>
        </div>
      </div>
    </div>
  )
}
